export interface Account {
  id: string;
  source: 'google' | 'local';
  source_account_id: string;
  display_name: string;
  status: 'active' | 'error' | 'revoked';
  error_message: string | null;
  connected_at: string | null;
  last_synced_at: string | null;
}

export interface Calendar {
  id: string;
  account_id: string;
  account_email: string;
  summary: string;
  background_color: string | null;
  foreground_color: string | null;
  is_primary: boolean;
  access_role: 'owner' | 'writer' | 'reader' | 'freeBusyReader' | null;
  visible: boolean;
  in_sidebar: boolean;
  keywords: string[];
  // Per-calendar sync diagnostics: set when this specific calendar failed
  // even though its account is otherwise fine. Account-level errors live on
  // Account.error_message.
  last_error: string | null;
  last_sync_at: string | null;
  // Watch channel state for Google Calendar push notifications. Null when
  // we've never registered a watch or the resource doesn't support push
  // (e.g. Google's en.canadian#holiday calendars). watch_error records the
  // last register/renew failure verbatim.
  watch_expiration: string | null;
  watch_error: string | null;
}

// Coarse push-delivery state derived per calendar. `na` means the system
// doesn't have a public origin configured (local dev or misconfigured
// production); `unsupported` means Google itself doesn't deliver push for
// this resource (don't alarm the user); `degraded` is the real warning —
// push should be working but the watch is expired, missing, or errored.
export type PushStatus = 'ok' | 'unsupported' | 'degraded' | 'na';

const UNSUPPORTED_RE = /not supported|pushNotSupported/i;

export function calendarPushStatus(c: Calendar, pushConfigured: boolean): PushStatus {
  if (!pushConfigured) return 'na';
  if (c.watch_error && UNSUPPORTED_RE.test(c.watch_error)) return 'unsupported';
  if (!c.watch_expiration) return 'degraded';
  if (new Date(c.watch_expiration).getTime() <= Date.now()) return 'degraded';
  return 'ok';
}

export interface CalendarEvent {
  id: string;
  calendar_id: string;
  summary: string | null;
  location: string | null;
  start_time: string | null;
  end_time: string | null;
  all_day: boolean;
  status: string | null;
  html_link: string | null;
  is_recurring_instance: boolean;
  // Local flag: 'once' counts down to this occurrence, 'series' moves to the
  // next one as each passes.
  countdown: CountdownMode | null;
  background_color: string | null;
  foreground_color: string | null;
}

export interface EventDetail extends CalendarEvent {
  description: string | null;
  time_zone: string | null;
  recurring_event_id: string | null;
}

export interface DailyNote {
  date: string; // YYYY-MM-DD
  exists: boolean;
  content: string;
  can_create: boolean;
}

export interface DailyNotesList {
  available: boolean;
  dates: string[]; // YYYY-MM-DD that have a note in the window
  today: string; // server's local today, YYYY-MM-DD
}

export interface PickerSession {
  session_id: string;
  picker_uri: string;
  poll_interval_ms: number;
  timeout_ms: number;
}

export type CountdownMode = 'once' | 'series';

export type EditScope = 'this' | 'following' | 'all';
export type DeleteScope = 'this' | 'following' | 'all';

export type View = 'month' | 'week' | 'day';

// A one-shot flash over a single day cell, replayed whenever `key` changes.
// The Today button and the countdown badges both use it to point at a day.
export interface DayPulse {
  day: Date;
  key: number;
}

export interface Preferences {
  theme: string;
  default_view: string;
  week_starts_on: number; // 0=Sun, 1=Mon
  default_calendar_id: string | null;
}
